/**
 * W5 (field test 2026-10-16) — the browser side of ONE hands-on timer + unattended operations.
 *
 * Every function here is a thin, typed door to ONE server authority. The browser decides nothing about timers:
 *   - switch_my_work               atomic pause-A (SWITCHED) [+ unattended op on A] + start/resume B
 *   - start/end_unattended_operation  equipment/process time on a vehicle; NEVER labor
 *   - my_hands_on_now              the caller's one running hands-on session (ticket or inspection)
 *   - technician_workspace_day     the typed read model behind "View <Technician>'s Bays"
 *
 * Idempotency: callers pass a request id they keep for the life of ONE human act (a retry or a double click
 * reuses it, so the server replays instead of acting twice).
 *
 * Never detach SupabaseClient methods: assigning `supabase.rpc` to a local drops its receiver and throws before any request.
 */
import { supabase } from '@/integrations/supabase/client';

export const UNATTENDED_KINDS = [
  'AC_CYCLE',
  'BATTERY_CHARGE',
  'PROGRAMMING',
  'AUTOMATED_DIAGNOSTIC',
  'LEAK_PRESSURE_HOLD',
  'OTHER',
] as const;
export type UnattendedKind = (typeof UNATTENDED_KINDS)[number];

export const UNATTENDED_KIND_LABEL: Record<UnattendedKind, string> = {
  AC_CYCLE: 'A/C machine cycle',
  BATTERY_CHARGE: 'Battery charging',
  PROGRAMMING: 'Module programming',
  AUTOMATED_DIAGNOSTIC: 'Automated diagnostic / scan',
  LEAK_PRESSURE_HOLD: 'Leak / pressure hold',
  OTHER: 'Other machine process',
};

export function unattendedKindLabel(kind: string | null | undefined): string {
  return (kind && (UNATTENDED_KIND_LABEL as Record<string, string>)[kind]) || 'Machine process';
}

export interface UnattendedRequest {
  kind: UnattendedKind;
  kindNote?: string | null;
  expectedMinutes?: number | null;
  relatedJobIds?: string[] | null;
}

export interface SwitchWorkRequest {
  requestId: string;
  fromTicketId: string;
  toTicketId: string;
  /** CAS: the session this device believes is running (null = only the ticket is checked). */
  expectedFromSessionId?: string | null;
  /** Required only when B is a NEW start (not a resume of my paused work on B). */
  toJobIds?: string[] | null;
  /** Leave A running on a machine: opens an unattended operation on A in the same transaction. */
  unattended?: UnattendedRequest | null;
}

export type SwitchWorkResult =
  | {
      outcome: 'SWITCHED';
      replayed: boolean;
      from: { ticketId: string; unattendedOperationId: string | null };
      to: { ticketId: string; outcome: string; ticketBecameRunning: boolean };
    }
  | { outcome: string; switchRefused: boolean; activeTicketId?: string | null; raw: Record<string, unknown> };

function newRequestId(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c?.randomUUID) return c.randomUUID();
  // RFC4122-ish fallback for very old browsers; the server only needs uniqueness.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    return (ch === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}
export { newRequestId };

function asRecord(v: unknown): Record<string, unknown> {
  return v && typeof v === 'object' ? (v as Record<string, unknown>) : {};
}

export async function switchMyWork(req: SwitchWorkRequest): Promise<SwitchWorkResult> {
  const { data, error } = await supabase.rpc('switch_my_work' as never, {
    p_request_id: req.requestId,
    p_from_ticket_id: req.fromTicketId,
    p_to_ticket_id: req.toTicketId,
    p_expected_from_session_id: req.expectedFromSessionId ?? null,
    p_to_job_ids: req.toJobIds && req.toJobIds.length > 0 ? req.toJobIds : null,
    p_unattended: req.unattended
      ? {
          kind: req.unattended.kind,
          kind_note: req.unattended.kindNote ?? null,
          expected_minutes: req.unattended.expectedMinutes ?? null,
          related_job_ids: req.unattended.relatedJobIds ?? null,
        }
      : null,
  } as never);
  if (error) {
    return { outcome: 'FAILED', switchRefused: true, raw: { message: error.message } };
  }
  const r = asRecord(data);
  if (r.outcome === 'SWITCHED') {
    const from = asRecord(r.from);
    const to = asRecord(r.to);
    return {
      outcome: 'SWITCHED',
      replayed: r.replayed === true,
      from: {
        ticketId: String(from.ticket_id ?? req.fromTicketId),
        unattendedOperationId: (from.unattended_operation_id as string | null | undefined) ?? null,
      },
      to: {
        ticketId: String(to.ticket_id ?? req.toTicketId),
        outcome: String(to.outcome ?? ''),
        ticketBecameRunning: to.ticket_became_running === true,
      },
    };
  }
  return {
    outcome: String(r.outcome ?? 'FAILED'),
    switchRefused: true,
    activeTicketId: (r.active_ticket_id as string | null | undefined) ?? null,
    raw: r,
  };
}

export interface UnattendedResult {
  outcome: string;
  operationId: string | null;
  replayed: boolean;
  raw: Record<string, unknown>;
}

export async function startUnattendedOperation(
  requestId: string,
  ticketId: string,
  op: UnattendedRequest,
): Promise<UnattendedResult> {
  const { data, error } = await supabase.rpc('start_unattended_operation' as never, {
    p_request_id: requestId,
    p_ticket_id: ticketId,
    p_kind: op.kind,
    p_kind_note: op.kindNote ?? null,
    p_expected_minutes: op.expectedMinutes ?? null,
    p_related_job_ids: op.relatedJobIds && op.relatedJobIds.length > 0 ? op.relatedJobIds : null,
  } as never);
  if (error) return { outcome: 'FAILED', operationId: null, replayed: false, raw: { message: error.message } };
  const r = asRecord(data);
  return {
    outcome: String(r.outcome ?? 'FAILED'),
    operationId: (r.operation_id as string | null | undefined) ?? null,
    replayed: r.replayed === true,
    raw: r,
  };
}

export async function endUnattendedOperation(
  requestId: string,
  operationId: string,
  endReason: 'COMPLETED' | 'ABORTED' = 'COMPLETED',
): Promise<UnattendedResult> {
  const { data, error } = await supabase.rpc('end_unattended_operation' as never, {
    p_request_id: requestId,
    p_operation_id: operationId,
    p_end_reason: endReason,
  } as never);
  if (error) return { outcome: 'FAILED', operationId, replayed: false, raw: { message: error.message } };
  const r = asRecord(data);
  return { outcome: String(r.outcome ?? 'FAILED'), operationId, replayed: r.replayed === true, raw: r };
}

export interface HandsOnNow {
  active: boolean;
  source?: 'ticket_timer' | 'inspection_timer';
  sessionId?: string;
  ticketId?: string;
  ticketNumber?: number | null;
  startedAt?: string;
}

export async function readMyHandsOnNow(shopId: string): Promise<HandsOnNow> {
  const { data, error } = await supabase.rpc('my_hands_on_now' as never, { p_shop_id: shopId } as never);
  if (error) return { active: false };
  const r = asRecord(data);
  if (r.active !== true) return { active: false };
  // NOV3 A6 (browser proof): the dialog names the REPAIR's number (a rollover day shows its head's), not the day row's.
  let ticketNumber = (r.ticket_number as number | null | undefined) ?? null;
  if (typeof r.ticket_id === 'string') {
    const { data: t } = await supabase.from('tickets').select('repair_number').eq('id', r.ticket_id).maybeSingle();
    const repair = (t as { repair_number?: number | null } | null)?.repair_number;
    if (typeof repair === 'number') ticketNumber = repair;
  }
  return {
    active: true,
    source: r.source as HandsOnNow['source'],
    sessionId: r.session_id as string,
    ticketId: r.ticket_id as string,
    ticketNumber,
    startedAt: r.started_at as string,
  };
}

/** Human copy for every switch / unattended outcome. Never shows a code. */
export function workSwitchOutcomeCopy(outcome: string): { tone: 'success' | 'warning' | 'error'; title: string; description?: string } {
  switch (outcome) {
    case 'SWITCHED':
      return { tone: 'success', title: 'Switched vehicles', description: 'The previous vehicle is paused — its measured time is kept.' };
    case 'STATE_CHANGED':
      return { tone: 'warning', title: 'Your work changed on another device', description: 'Nothing was switched. Check what is running now.' };
    case 'NO_ACTIVE_TIMER':
      return { tone: 'warning', title: 'Nothing is running to switch from', description: 'Start or resume the vehicle directly.' };
    case 'ALREADY_HAS_ACTIVE_TIMER':
      return { tone: 'warning', title: 'You already have hands-on work running' };
    case 'IDEMPOTENCY_KEY_REUSED':
      return { tone: 'error', title: 'That request was already used for something else', description: 'Nothing was switched. Try again.' };
    case 'EPISODE_AWAITING_REVIEW':
    case 'REVIEW_REQUIRED_BEFORE_NEW_TIMER':
      return { tone: 'warning', title: 'Time review required first', description: 'Your current work keeps running.' };
    case 'SCOPE_STALE':
    case 'SCOPE_REVIEW_REQUIRED':
    case 'INVALID_JOB_SCOPE':
      return { tone: 'warning', title: 'Choose the jobs for that vehicle first', description: 'Your current work keeps running.' };
    case 'CUSTODY_WITH_FRONT_OFFICE':
      return { tone: 'warning', title: 'That vehicle is with the advisor', description: 'Your current work keeps running.' };
    case 'AUTHORIZATION_REQUIRED':
    case 'ESTIMATE_REQUIRED':
      return { tone: 'warning', title: 'That vehicle needs customer authorization', description: 'The front office has been told. Your current work keeps running.' };
    case 'VEHICLE_NOT_PRESENT':
      return { tone: 'warning', title: 'That vehicle is not checked in', description: 'Your current work keeps running.' };
    case 'VEHICLE_NOT_IN_BAY':
      return { tone: 'warning', title: 'A machine process needs the vehicle in a bay' };
    case 'NOT_WORKING_THIS_TICKET':
      return { tone: 'warning', title: 'Only the technician working this vehicle can start a machine process on it' };
    case 'NOTE_REQUIRED_FOR_OTHER':
      return { tone: 'warning', title: 'Say what the machine is doing' };
    case 'INVALID_EXPECTED_MINUTES':
      return { tone: 'warning', title: 'Expected minutes must be between 1 and 1440' };
    case 'ALREADY_OPEN':
      return { tone: 'warning', title: 'That machine process is already running on this vehicle' };
    case 'OPENED':
      return { tone: 'success', title: 'Machine process started', description: 'Unattended time — no labor is recorded for it.' };
    case 'ENDED':
      return { tone: 'success', title: 'Machine process ended' };
    case 'ALREADY_ENDED':
      return { tone: 'warning', title: 'That machine process had already ended' };
    case 'NOT_PERMITTED':
      return { tone: 'warning', title: 'Only the technician who started it, or a manager, can end it' };
    case 'TICKET_NOT_STARTABLE':
    case 'ESTIMATE_NOT_STARTABLE':
      return { tone: 'warning', title: 'That ticket cannot take work right now' };
    case 'WORK_ROLE_REFUSED':
      return { tone: 'warning', title: 'That work is outside your work roles', description: 'Your current work keeps running.' };
    // NOV3: a parked target is brought into a bay by its own Start, not by a switch (nothing was paused).
    case 'PARKED_PULL_IN_REQUIRED':
      return { tone: 'warning', title: 'That vehicle is in the parking lot', description: 'Nothing was switched. Pause this work, then press Start on that vehicle to bring it into a bay.' };
    case 'TARGET_REFUSED':
      return { tone: 'warning', title: 'That vehicle cannot take work right now', description: 'Nothing was switched — your current work keeps running.' };
    // NOV3: the vehicle you were working on is a day of a repair that was carried forward (or the target is).
    case 'SEGMENT_ROLLED_FORWARD':
      return { tone: 'warning', title: 'That day of the repair is closed', description: 'The work was carried forward to its next day — start or resume that day’s ticket.' };
    default:
      return { tone: 'error', title: 'Nothing was changed', description: 'Try again — if it keeps happening, tell a manager.' };
  }
}

/** Ask the Dashboard to offer a vehicle switch after a refused Start/Resume (see WorkSwitchHost). */
export const OFFER_WORK_SWITCH_EVENT = 'optibay:offer-work-switch';
export interface OfferWorkSwitchDetail {
  toTicketId: string;
  shopId: string | null;
  /** The job set the person already chose for a NEW start on the target (null for a resume). */
  toJobIds: string[] | null;
}
export function offerWorkSwitch(detail: OfferWorkSwitchDetail): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent<OfferWorkSwitchDetail>(OFFER_WORK_SWITCH_EVENT, { detail }));
}

/**
 * After ONE timer act's result: when the server refused a Start/Resume because the person already measures other
 * hands-on work, offer the atomic switch. Called unconditionally by the timer surface (it never reads outcomes).
 */
export function offerWorkSwitchAfterRefusal(
  request: { intent: string; ticketId: string; jobIds?: readonly string[] },
  result: { outcome: string },
  shopId: string | null,
): boolean {
  if (result.outcome !== 'ALREADY_HAS_ACTIVE_TIMER') return false;
  if (request.intent !== 'START' && request.intent !== 'RESUME') return false;
  offerWorkSwitch({
    toTicketId: request.ticketId,
    shopId,
    toJobIds: request.intent === 'START' && request.jobIds ? [...request.jobIds] : null,
  });
  return true;
}
